import { z } from 'zod';

// All VITE_-prefixed environment variables required by the app
export interface EnvConfig {
  VITE_COGNITO_USER_POOL_ID: string;
  VITE_COGNITO_CLIENT_ID: string;
  VITE_COGNITO_DOMAIN: string;
  VITE_API_GATEWAY_BASE_URL: string;
  VITE_SCANNER_API_BASE_URL: string;
  VITE_CLOUDFRONT_DOMAIN: string;
  VITE_ENVIRONMENT: 'development' | 'production';
}

const envSchema = z.object({
  VITE_COGNITO_USER_POOL_ID: z.string().min(1),
  VITE_COGNITO_CLIENT_ID: z.string().min(1),
  VITE_COGNITO_DOMAIN: z.string().min(1),
  VITE_API_GATEWAY_BASE_URL: z.string().url(),
  VITE_SCANNER_API_BASE_URL: z.string().url(),
  VITE_CLOUDFRONT_DOMAIN: z.string().min(1),
  VITE_ENVIRONMENT: z.enum(['development', 'production']),
});

export type EnvValidationResult =
  | { success: true; config: EnvConfig }
  | { success: false; missingVars: string[] };

/**
 * Validates all required environment variables at application startup.
 * Returns a typed config on success, or an error listing every missing variable.
 */
export function validateEnvConfig(
  env: Record<string, string | undefined> = import.meta.env,
): EnvValidationResult {
  const result = envSchema.safeParse(env);

  if (result.success) {
    return { success: true, config: result.data };
  }

  // Collect the names of every field that failed validation
  const missingVars = result.error.issues.map((issue) => String(issue.path[0]));

  return { success: false, missingVars };
}
